import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  FareCalculationNotFoundException,
  FileData,
  PhoneNumberUtil,
  ServiceException,
  TextDetectionResult,
  isNullOrUndefined
} from '@uzurv/core';
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { CountryCode } from 'libphonenumber-js';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  CancellationReason,
  DeviceInstallation,
  Driver,
  DriverEarnings,
  DriverStatus,
  OfflineRouteNode,
  PositionUpdate,
  Release,
  Reservation,
  ReservationRouteSummary,
  ReservationStatusUpdate,
  RiderFare,
  RiderProtectedReservationDetail,
  RuleValue,
  TollReimbursement,
  Provider
} from '../../models';

@Injectable({
  providedIn: 'root'
})
export class ProviderDriverService extends UzurvRestService {
  readonly resourceUrl = 'providerDrivers/';

  // Endpoint RegExps
  static readonly GET_RESERVATION_BY_ID: RegExp = /providerDrivers\/[0-9]*\/reservations\/([0-9]*)\/?$/;
  static readonly RESERVATION_STATUS_UPDATE_URL_REGEX: RegExp = /providerDrivers\/[0-9]*\/reservations\/([0-9]*)\/reservationStatus/;
  static readonly SYNC_QUEUED_ROUTE_NODES_URL_REGEX: RegExp = /providerDrivers\/[0-9]*\/queuedRouteNodes/;
  static readonly RIDER_FARE_URL_REGEX: RegExp = /providerDrivers\/[0-9]*\/reservations\/[0-9]*\/riderFares\/?(?:\?multi=true)?/;
  static readonly TOLL_REIMBURSEMENT_URL_REGEX: RegExp = /providerDrivers\/[0-9]*\/reservations\/([0-9]*)\/tollReimbursements\/?$/;
  static readonly DRIVER_POSITION_UPDATE_URL_REGEX: RegExp = /providerDrivers\/[0-9]*\/driverPosition/;

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /*************************** Provider Driver ********************************/

  /**
   * Finds a driver by their id
   *
   * @param providerDriverId
   * @permission driver
   */
  findProviderDriverById(providerDriverId: number): Observable<Driver> {
    return this.get(`${this.resourceUrl}${providerDriverId}`).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * Updates an existing provider driver
   *
   * @param providerDriverId
   * @param patchDictionary
   * @permission provider_driver
   */
  patchProviderDriver(providerDriverId: number, patchDictionary: object): Observable<Driver> {
    if (!patchDictionary.hasOwnProperty('id')) {
      patchDictionary = {
        ...patchDictionary,
        id: providerDriverId
      };
    }

    if (!isNullOrUndefined(patchDictionary['phoneNumber'])) {
      let countryCode: CountryCode = PhoneNumberUtil.getCountryCode(patchDictionary['phoneNumber']);

      if (isNullOrUndefined(countryCode)) {
        countryCode = 'US';
      }

      patchDictionary = {
        ...patchDictionary,
        phoneNumber: PhoneNumberUtil.formatPhoneNumberForRest(patchDictionary['phoneNumber'], countryCode)
      };
    }

    return this.patch<Driver>(`${this.resourceUrl}${providerDriverId}`, patchDictionary).pipe(
      map(response => Deserialize(response, Driver))
    );
  }

  /**
   * Deletes a provider driver's account.
   *
   * This action is irreversable!
   *
   * @param providerDriverId
   * @permission provider_driver
   */
  deleteAccount(providerDriverId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${providerDriverId}`).pipe(map(() => true));
  }

  /**************** Provider ***************/

  /**
   * Finds a driver by their id
   *
   * @param providerDriverId
   * @permission driver
   */
  findProviderByDriverId(providerDriverId: number): Observable<Provider> {
    return this.get(`${this.resourceUrl}${providerDriverId}/provider`).pipe(map(response => Deserialize(response, Provider)));
  }

  /**************** Driver Status ***************/

  /**
   * Get the driver's current status
   *
   * @param {number} providerDriverId
   * @permission driver
   * @returns {Observable<DriverStatus>}
   * @memberof DriverService
   */
  getDriverStatus(providerDriverId: number): Observable<DriverStatus> {
    if (isNullOrUndefined(providerDriverId)) {
      return throwError(() => new ServiceException('providerDriverId cannot be null'));
    }

    return this.get(`${this.resourceUrl}${providerDriverId}/currentStatus/`).pipe(map(response => Deserialize(response, DriverStatus)));
  }

  /*************************** Rule Values ********************************/

  /**
   * Returns a list of rule values
   *
   */
  getRuleValues(): Observable<Array<RuleValue>> {
    return this.get(`${this.resourceUrl}ruleValues/`, { pageNumber: 0, pageSize: 1000 }).pipe(
      map(response => Deserialize(response, RuleValue))
    );
  }

  /**
   * Returns a rule value by ID
   *
   * @param id
   */
  findRuleValueWithId(id: number): Observable<RuleValue> {
    return this.getRuleValues().pipe(
      map(response => {
        return response.find(ruleValue => ruleValue.id === id);
      })
    );
  }

  /**
   * Returns a rule value by name
   *
   * @param name
   */
  findRuleValueWithName(name: string): Observable<RuleValue> {
    return this.getRuleValues().pipe(
      map(response => {
        return response.find(ruleValue => ruleValue.name === name);
      })
    );
  }

  /*************************** Reservations ********************************/

  /**
   * Returns a list of reservations by driver ID
   *
   * @param providerDriverId
   * @param pageNumber
   * @param pageSize
   * @param inactive
   */
  listReservationsByProviderDriverIdPaginated(
    providerDriverId: number,
    pageNumber: number = 0,
    pageSize: number = 10,
    inactive: boolean = null
  ): Observable<Array<RiderProtectedReservationDetail>> {
    const params = {
      pageNumber: pageNumber,
      pageSize: pageSize
    };

    if (!isNullOrUndefined(inactive)) {
      params['inactive'] = inactive;
    }

    return this.get<Array<RiderProtectedReservationDetail>>(`${this.resourceUrl}${providerDriverId}/reservations/`, params).pipe(
      map(response => Deserialize(response, RiderProtectedReservationDetail))
    );
  }

  /**
   * Returns a reservation by providerDriverId and reservationId
   * @param providerDriverId
   * @param reservationId
   * @permission driver
   */
  findReservationById(providerDriverId: number, reservationId: number): Observable<RiderProtectedReservationDetail> {
    return this.get<RiderProtectedReservationDetail>(`${this.resourceUrl}${providerDriverId}/reservations/${reservationId}`).pipe(
      map(response => Deserialize(response, RiderProtectedReservationDetail))
    );
  }

  /**
   * Returns a reservation route summary by reservation ID
   *
   * @param providerDriverId
   * @param reservationId
   * @param routePeriodCode
   */
  findReservationRouteSummaryByReservationId(
    providerDriverId: number,
    reservationId: number,
    routePeriodCode: string
  ): Observable<ReservationRouteSummary> {
    return this.get<ReservationRouteSummary>(
      `${this.resourceUrl}${providerDriverId}/reservations/${reservationId}/reservationRouteSummary/`,
      {
        routePeriodCode: routePeriodCode
      }
    ).pipe(map(response => Deserialize(response, ReservationRouteSummary)));
  }

  /**
   * Updates a reservation with a new reservation status and geo-coordinates
   *
   * @param providerDriverId
   * @param reservationId
   * @param reservationStatusName
   * @param latitude
   * @param longitude
   * @param deviceInstallationId
   */
  updateReservationStatus(
    providerDriverId: number,
    reservationId: number,
    reservationStatusName: string,
    latitude: number,
    longitude: number,
    deviceInstallationId: number
  ): Observable<RiderProtectedReservationDetail> {
    const reservationStatusUpdate: ReservationStatusUpdate = new ReservationStatusUpdate();
    reservationStatusUpdate.reservationId = reservationId;
    reservationStatusUpdate.reservationStatusName = reservationStatusName;
    reservationStatusUpdate.timestamp = new Date();
    reservationStatusUpdate.latitude = latitude;
    reservationStatusUpdate.longitude = longitude;
    reservationStatusUpdate.deviceInstallationId = deviceInstallationId;

    return this.put<RiderProtectedReservationDetail>(
      `${this.resourceUrl}${providerDriverId}/reservations/${reservationId}/reservationStatus/`,
      Serialize(reservationStatusUpdate)
    ).pipe(map(response => Deserialize(response, RiderProtectedReservationDetail)));
  }

  /**
   * Gets a temporary generated phone number by reservation id
   *
   * @param providerDriverId
   * @param reservationId
   */
  getGeneratedPhoneNumberByReservationId(providerDriverId: number, reservationId: number): Observable<string> {
    return this.post<string>(`${this.resourceUrl}${providerDriverId}/reservations/${reservationId}/phoneCalls/`, null);
  }

  /**
   * Assigns a ride to the given driver
   *
   * @param providerDriverId
   * @param reservation
   */
  assignReservation(providerDriverId: number, reservation: Reservation): Observable<RiderProtectedReservationDetail> {
    return this.patch<RiderProtectedReservationDetail>(
      `${this.resourceUrl}${providerDriverId}/reservations/${reservation.id}`,
      Serialize(reservation)
    ).pipe(map(response => Deserialize(response, RiderProtectedReservationDetail)));
  }

  /**
   * Unassigns a ride from the given driver and assigns it back to the proxy driver
   *
   * @param providerDriverId
   * @param reservationId
   * @returns
   */
  unassignReservation(providerDriverId: number, reservationId: number): Observable<RiderProtectedReservationDetail> {
    return this.delete<RiderProtectedReservationDetail>(`${this.resourceUrl}${providerDriverId}/reservations/${reservationId}`).pipe(
      map(response => Deserialize(response, RiderProtectedReservationDetail))
    );
  }

  /*************************** Cancellation ********************************/

  /**
   * Returns a list of cancellation reasons.
   *
   */
  listReservationCancellationReasons(): Observable<Array<CancellationReason>> {
    return this.get<Array<CancellationReason>>(`${this.resourceUrl}cancellationReasons/`).pipe(
      map(response => Deserialize(response, CancellationReason))
    );
  }

  /**
   * Returns a cancellation reason by ID
   *
   * @param id
   */
  findCancellationReasonWithId(id: number): Observable<CancellationReason> {
    return this.listReservationCancellationReasons().pipe(
      map(response => response.find(cancellationReason => cancellationReason.id === id))
    );
  }

  /**
   * Returns a cancellation reason by name
   *
   * @param name
   */
  findCancellationReasonWithName(name: string): Observable<CancellationReason> {
    return this.listReservationCancellationReasons().pipe(
      map(response => response.find(cancellationReason => cancellationReason.name === name))
    );
  }

  /**
   * Cancels a reservation with a cancellation reason
   *
   * @param providerDriverId
   * @param reservationId
   * @param cancellationReason
   */
  cancelReservationWithCancellationReason(
    providerDriverId: number,
    reservationId: number,
    cancellationReason: CancellationReason
  ): Observable<Reservation> {
    const patchDictionary = {
      id: reservationId,
      cancelled: true,
      cancellationReason: cancellationReason
    };

    return this.patch<Reservation>(`${this.resourceUrl}${providerDriverId}/reservations/${reservationId}`, patchDictionary).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /*************************** Offline Ride Execution ********************************/

  /**
   * Saves a list of queued route nodes
   *
   * @param providerDriverId
   * @param routeNodes
   */
  saveQueuedRouteNodes(providerDriverId: number, routeNodes: Array<OfflineRouteNode>) {
    return this.post<boolean>(`${this.resourceUrl}${providerDriverId}/queuedRouteNodes/`, Serialize(routeNodes)).pipe(map(() => true));
  }

  /************************************* Device Installations *************************/

  /**
   * Returns a list of device installations for a user
   *
   * @param providerDriverId
   */
  getDeviceInstallations(providerDriverId: number): Observable<Array<DeviceInstallation>> {
    return this.get<Array<DeviceInstallation>>(`${this.resourceUrl}${providerDriverId}/deviceInstallations/`).pipe(
      map(response => Deserialize(response, DeviceInstallation))
    );
  }

  /**
   * Creates a new device installation for a user
   *
   * @param providerDriverId
   * @param deviceInstallation
   */
  createDeviceInstallation(providerDriverId: number, deviceInstallation: DeviceInstallation): Observable<DeviceInstallation> {
    return this.post<DeviceInstallation>(`${this.resourceUrl}${providerDriverId}/deviceInstallations/`, Serialize(deviceInstallation)).pipe(
      map(response => Deserialize(response, DeviceInstallation))
    );
  }

  /**
   * Updates an existing device installation
   *
   * @param providerDriverId
   * @param deviceInstallation
   */
  updateDeviceInstallation(providerDriverId: number, deviceInstallation: DeviceInstallation): Observable<DeviceInstallation> {
    return this.put<DeviceInstallation>(
      `${this.resourceUrl}${providerDriverId}/deviceInstallations/${deviceInstallation.id}`,
      Serialize(deviceInstallation)
    ).pipe(map(response => Deserialize(response, DeviceInstallation)));
  }

  /************************************* Driver Position *************************/

  /**
   * PUTs the driver's location by ID, latitude, and longitude. Will default to
   * the current timestamp, or one can be supplied
   *
   *
   * @param providerDriverId
   * @param latitude
   * @param longitude
   * @param timestamp
   * @param deviceInstallationId
   * @permission driver
   */
  updateDriverPosition(
    providerDriverId: number,
    latitude: number,
    longitude: number,
    timestamp: Date,
    deviceInstallationId: number
  ): Observable<null> {
    const positionUpdate = new PositionUpdate();
    positionUpdate.personId = providerDriverId;
    positionUpdate.latitude = latitude;
    positionUpdate.longitude = longitude;
    positionUpdate.timestamp = timestamp;
    positionUpdate.deviceInstallationId = deviceInstallationId;

    return this.put(`${this.resourceUrl}${providerDriverId}/driverPosition`, Serialize(positionUpdate));
  }

  /************************************* Rider Payment Collection ***********************/

  listRiderFaresByReservationId(providerDriverId: number, reservationId: number): Observable<Array<RiderFare>> {
    return this.get<Array<RiderFare>>(`${this.resourceUrl}${providerDriverId}/reservations/${reservationId}/riderFares/`).pipe(
      map(response => Deserialize(response, RiderFare))
    );
  }

  detectTextFromImage(id: number, image: FileData): Observable<Array<TextDetectionResult>> {
    return this.post(`${this.resourceUrl}${id}/detectTextFromImage/`, Serialize(image)).pipe(
      map(response => Deserialize(response, TextDetectionResult))
    );
  }

  saveRiderFare(providerDriverId: number, reservationId: number, riderFare: RiderFare): Observable<RiderFare> {
    return this.post<RiderFare>(
      `${this.resourceUrl}${providerDriverId}/reservations/${reservationId}/riderFares/`,
      Serialize(riderFare)
    ).pipe(map(response => Deserialize(response, RiderFare)));
  }

  saveRiderFares(providerDriverId: number, reservationId: number, riderFares: Array<RiderFare>): Observable<Array<RiderFare>> {
    return this.post<Array<RiderFare>>(
      `${this.resourceUrl}${providerDriverId}/reservations/${reservationId}/riderFares/?multi=true`,
      Serialize(riderFares)
    ).pipe(map(response => Deserialize(response, RiderFare)));
  }

  /**
   * Returns driver earnings by reservation ID
   *
   * @param providerDriverId
   * @param reservationId
   */
  getDriverEarnings(providerDriverId: number, reservationId: number): Observable<DriverEarnings> {
    return this.get<DriverEarnings>(`${this.resourceUrl}${providerDriverId}/reservations/${reservationId}/driverEarnings/`)
      .pipe(map(response => Deserialize(response, DriverEarnings)))
      .pipe(
        catchError(err => {
          const message: string = isNullOrUndefined(err.message) ? '' : err.message;

          if (message.includes('FareCalculation for Reservation with id') && message.includes('not found')) {
            return throwError(new FareCalculationNotFoundException(message));
          }

          return throwError(err);
        })
      );
  }

  // Toll Reimbursements

  /**
   * Gets a list of all pending toll reimbursements for the given driver id
   *
   * @param providerDriverId
   */
  listPendingTollReimbursementsByDriverId(providerDriverId: number): Observable<Array<TollReimbursement>> {
    return this.get(`${this.resourceUrl}${providerDriverId}/tollReimbursements/`).pipe(
      map(response => Deserialize(response, TollReimbursement))
    );
  }

  /**
   * Submits a toll reimbursement for a driver
   *
   * @param driverId
   * @param tollReimbursement
   */
  submitTollReimbursement(providerDriverId: number, tollReimbursement: TollReimbursement): Observable<TollReimbursement> {
    return this.put(`${this.resourceUrl}${providerDriverId}/tollReimbursements/`, tollReimbursement, {
      reservationId: tollReimbursement.reservationId
    }).pipe(map(response => Deserialize(response, TollReimbursement)));
  }

  /************************************* Releases ***********************/

  findReleaseByAppBundleIdentifierReleasePlatformCodeAndVersion(
    wavDriverId: number,
    appBundleIdentifier: string,
    releasePlatformCode: string,
    version: string
  ): Observable<Release> {
    return this.get<Array<Release>>(`${this.resourceUrl}${wavDriverId}/releases/`, {
      appBundleIdentifier: appBundleIdentifier,
      releasePlatformCode: releasePlatformCode,
      version: version
    }).pipe(map(response => Deserialize(response, Release)));
  }
}
